import React, { useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Avatar,
  Divider,
  Alert
} from 'antd';
import { UserOutlined, MailOutlined, BankOutlined, IdcardOutlined } from '@ant-design/icons';

import type { User } from '../../../types/user';
import { ValidationRules } from '../../../types/user';

const { Title, Text } = Typography;

interface BasicInfoTabProps {
  user: User | null;
  mode: 'create' | 'edit' | 'view';
  onChange: (data: Partial<User>) => void;
  errors: Record<string, string[]>;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  user,
  mode,
  onChange,
  errors
}) => {
  const [form] = Form.useForm();
  const isReadOnly = mode === 'view';

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        userId: user.userId,
        userName: user.userName,
        email: user.email,
        dptNo: user.dptNo,
        positionId: user.positionId,
        isActive: user.isActive,
      });
    }
  }, [user, form]);

  // Handle form field changes
  const handleFieldChange = (changedFields: any) => {
    const changes: Partial<User> = {};
    
    changedFields.forEach((field: any) => {
      const { name, value } = field;
      if (Array.isArray(name) && name.length === 1) {
        changes[name[0] as keyof User] = value;
      }
    });

    if (Object.keys(changes).length > 0) {
      onChange(changes);
    }
  };

  return (
    <div>
      {/* Header */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col span={24}>
          <Space align="center" size="large">
            <Avatar 
              size={80} 
              icon={<UserOutlined />}
              style={{ 
                backgroundColor: user?.isActive ? '#1890ff' : '#d9d9d9',
                fontSize: '32px'
              }}
            >
              {user?.userName?.charAt(0) || 'U'}
            </Avatar>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {mode === 'create' ? '新使用者' : user?.userName || '使用者'}
              </Title>
              <Text type="secondary">
                {mode === 'create' ? '請填寫基本資訊' : `ID: ${user?.userId || 'N/A'}`}
              </Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* Form */}
      <Form
        form={form}
        layout="vertical"
        onFieldsChange={handleFieldChange}
        disabled={isReadOnly}
        size="large"
      >
        <Row gutter={[24, 16]}>
          {/* Basic Information Card */}
          <Col span={24}>
            <Card 
              title={
                <Space>
                  <UserOutlined style={{ color: '#1890ff' }} />
                  <span>基本資訊</span>
                </Space>
              }
              bordered={false}
              style={{ background: '#fafafa' }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="userId"
                    label="使用者ID"
                    rules={[
                      { required: true, message: '請輸入使用者ID' },
                      { 
                        pattern: ValidationRules.userId.pattern,
                        message: ValidationRules.userId.message
                      }
                    ]}
                    validateStatus={errors.userId ? 'error' : ''}
                    help={errors.userId?.[0]}
                  >
                    <Input 
                      placeholder="輸入使用者ID (3-20位英數字或底線)"
                      prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                      disabled={mode === 'edit'} // ID cannot be changed in edit mode
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="userName"
                    label="使用者姓名"
                    rules={[
                      { required: true, message: '請輸入使用者姓名' },
                      { max: 30, message: '姓名最多30個字符' }
                    ]}
                    validateStatus={errors.userName ? 'error' : ''}
                    help={errors.userName?.[0]}
                  >
                    <Input 
                      placeholder="輸入使用者姓名"
                      prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="電子郵件"
                    rules={[
                      { type: 'email', message: '請輸入有效的電子郵件地址' }
                    ]}
                    validateStatus={errors.email ? 'error' : ''}
                    help={errors.email?.[0]}
                  >
                    <Input 
                      placeholder="輸入電子郵件地址"
                      prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="dptNo"
                    label="部門編號"
                    rules={[
                      { required: true, message: '請輸入部門編號' },
                      {
                        pattern: /^[A-Za-z0-9]{1,20}$/,
                        message: '部門編號必須為英數字，最多20位'
                      }
                    ]}
                    validateStatus={errors.dptNo ? 'error' : ''}
                    help={errors.dptNo?.[0]}
                  >
                    <Input
                      placeholder="輸入部門編號 (如 D001)"
                      prefix={<BankOutlined style={{ color: '#8c8c8c' }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="positionId"
                    label="職位ID"
                    rules={[
                      { required: true, message: '請輸入職位ID' },
                    ]}
                    validateStatus={errors.positionId ? 'error' : ''}
                    help={errors.positionId?.[0]}
                  >
                    <InputNumber
                      placeholder="輸入職位ID (如 1)"
                      prefix={<IdcardOutlined style={{ color: '#8c8c8c' }} />}
                      min={1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="isActive"
                    label="帳號狀態"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="啟用"
                      unCheckedChildren="停用"
                      defaultChecked
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

        </Row>

        {/* Additional Information for Edit/View Mode */}
        {mode !== 'create' && user && (
          <Row gutter={[24, 16]} style={{ marginTop: '24px' }}>
            <Col span={24}>
              <Card 
                title="系統資訊"
                bordered={false}
                style={{ background: '#f9f9f9' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div>
                      <Text type="secondary">建立時間</Text>
                      <div style={{ marginTop: '4px' }}>
                        <Text>
                          {user.createdAt ? new Date(user.createdAt).toLocaleString('zh-TW') : 'N/A'}
                        </Text>
                      </div>
                    </div>
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <div>
                      <Text type="secondary">最後更新時間</Text>
                      <div style={{ marginTop: '4px' }}>
                        <Text>
                          {user.updatedAt ? new Date(user.updatedAt).toLocaleString('zh-TW') : 'N/A'}
                        </Text>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div>
                      <Text type="secondary">最後登入時間</Text>
                      <div style={{ marginTop: '4px' }}>
                        <Text>
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('zh-TW') : '從未登入'}
                        </Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {/* Password Information for Create Mode */}
        {mode === 'create' && (
          <Row gutter={[24, 16]} style={{ marginTop: '24px' }}>
            <Col span={24}>
              <Alert
                message="密碼資訊"
                description="新建立的使用者將使用預設密碼 'Temp123456'，首次登入時需要修改密碼。"
                type="info"
                showIcon
              />
            </Col>
          </Row>
        )}
      </Form>
    </div>
  );
};

export default BasicInfoTab;